import { useQuery, useQueries, useQueryClient } from 'react-query';
import { Options } from '@frontend/fetch';
import { LimitedSchemaQueryOptions, LimitedSchemaFetchQueryOptions } from '@frontend/react-query-helpers';
import { SchemaSMSDataV4Service } from '../service';
import { GetThreadIdIO } from '../types';

export const getGetThreadIdQueryKey = (req: GetThreadIdIO['input']) => ['V4', 'GetThreadId', req] as const;
type QueryKey = ReturnType<typeof getGetThreadIdQueryKey>;

/**
 * A hook that returns a query for the `GetThreadId` endpoint.
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useGetThreadIdQuery = <E = unknown, D = GetThreadIdIO['output']>(
  req: GetThreadIdIO['input'],
  options?: LimitedSchemaQueryOptions<GetThreadIdIO, E, D, QueryKey>,
  httpOptions?: Options
) =>
  useQuery<GetThreadIdIO['output'], E, D, QueryKey>({
    queryKey: getGetThreadIdQueryKey(req),
    queryFn: () => SchemaSMSDataV4Service.GetThreadId(req, httpOptions),
    ...options,
  });

/**
 * A hook that returns an array of queries for the `GetThreadId` endpoint.
 * It takes an array of objects with the following properties:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useGetThreadIdQueries = <E = unknown, D = GetThreadIdIO['output']>(
  argsArr: {
    req: GetThreadIdIO['input'];
    options?: LimitedSchemaQueryOptions<GetThreadIdIO, E, D, QueryKey>;
    httpOptions?: Options;
  }[]
) =>
  useQueries(
    argsArr.map(({ req, options, httpOptions }) => ({
      queryKey: getGetThreadIdQueryKey(req),
      queryFn: () => SchemaSMSDataV4Service.GetThreadId(req, httpOptions),
      ...options,
    }))
  ) as ReturnType<typeof useGetThreadIdQuery<E, D>>[];

/**
 * A hook that returns a function to fetch the query for the `GetThreadId` endpoint.
 * It uses `queryClient.fetchQuery` to ensure proper caching behavior.
 * NOTE: this should only be used in specific scenarios where you need to manually fetch the query. Otherwise,
 * `useQuery` should be used. Manual error and loading states need to be handled when using this function.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useFetchGetThreadId = () => {
  const queryClient = useQueryClient();

  const fetchGetThreadId = async <E = unknown, D = GetThreadIdIO['output']>(
    req: GetThreadIdIO['input'],
    options?: LimitedSchemaFetchQueryOptions<GetThreadIdIO, E, D, QueryKey>,
    httpOptions?: Options
  ) => {
    const response = await queryClient.fetchQuery<GetThreadIdIO['output'], E, D, QueryKey>(
      getGetThreadIdQueryKey(req),
      () => SchemaSMSDataV4Service.GetThreadId(req, httpOptions),
      options
    );

    return response;
  };

  return fetchGetThreadId;
};
